import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import {
  registerAsTutor, getTutorProfile, getOnboardingLink, getStripeStatus,
  uploadTutorCredential,
} from '../api/endpoints';
import {
  GraduationCap, DollarSign, Globe, Clock, BookOpen, Shield,
  ChevronRight, ChevronLeft, Check, AlertCircle, ExternalLink,
  Plus, X, Loader2, Upload, FileText, Trash2
} from 'lucide-react';
import Icon3D from '../components/Icon3D';

const LANGUAGES = [
  'English', 'Mandarin', 'Hindi', 'Spanish', 'Arabic', 'Malay',
  'French', 'Portuguese', 'Japanese', 'Korean', 'German', 'Italian',
  'Vietnamese', 'Thai', 'Indonesian', 'Tagalog', 'Tamil', 'Urdu'
];

const EDUCATION_LEVELS = [
  { value: 'university', label: 'University' },
  { value: 'postgrad', label: 'Postgraduate' },
];

const SESSION_DURATION_OPTIONS = [30, 60, 90];

const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const TutorRegistrationPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if returning from Stripe onboarding refresh
  const isRefresh = searchParams.get('refresh') === 'true';

  // Steps
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [existingProfile, setExistingProfile] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);

  // Form state — Step 1: Profile
  const [bio, setBio] = useState('');
  const [educationLevel, setEducationLevel] = useState('university');
  const [languages, setLanguages] = useState(['English']);
  const [preferredLanguage, setPreferredLanguage] = useState('English');
  const [newLanguage, setNewLanguage] = useState('');

  // Form state — Step 2: Subjects & Pricing
  const [subjects, setSubjects] = useState([]);
  const [newSubject, setNewSubject] = useState('');
  const [completedSubjects, setCompletedSubjects] = useState([]);
  const [newCompletedSubject, setNewCompletedSubject] = useState('');
  const [hourlyRate, setHourlyRate] = useState('35');
  const [sessionDurations, setSessionDurations] = useState([60]);

  // Form state — Step 3: Availability
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [availability, setAvailability] = useState({});

  // Form state — Step 4: Credentials
  const [credentials, setCredentials] = useState([]); // uploaded credentials
  const [uploadingCredential, setUploadingCredential] = useState(false);

  // Check existing tutor profile on mount
  useEffect(() => {
    checkExistingProfile();
  }, []);

  const checkExistingProfile = async () => {
    setLoading(true);
    try {
      const res = await getTutorProfile();
      setExistingProfile(res.data);

      // Load existing credentials
      if (res.data.credential_uploads) {
        setCredentials(res.data.credential_uploads);
      }

      // If profile exists, check Stripe status
      if (res.data.stripe_connect_account_id) {
        try {
          const stripeRes = await getStripeStatus();
          setStripeStatus(stripeRes.data);
        } catch {
          // Stripe status check failed, not critical
        }
      }

      // If returning from Stripe refresh, redirect to get a new onboarding link
      if (isRefresh && res.data.stripe_connect_status !== 'active') {
        setCurrentStep(4); // Go to Stripe step
      } else if (res.data.stripe_connect_status === 'active') {
        // Fully onboarded, redirect to dashboard
        navigate('/tutoring/dashboard');
        return;
      } else {
        setCurrentStep(4); // Profile exists but Stripe not complete
      }
    } catch {
      // No existing profile — start registration
      setExistingProfile(null);
    } finally {
      setLoading(false);
    }
  };

  // Add/remove helpers
  const addSubject = () => {
    const trimmed = newSubject.trim();
    if (trimmed && !subjects.includes(trimmed)) {
      setSubjects([...subjects, trimmed]);
      setNewSubject('');
    }
  };

  const removeSubject = (subject) => {
    setSubjects(subjects.filter(s => s !== subject));
  };

  const addCompletedSubject = () => {
    const trimmed = newCompletedSubject.trim();
    if (trimmed && !completedSubjects.includes(trimmed)) {
      setCompletedSubjects([...completedSubjects, trimmed]);
      setNewCompletedSubject('');
    }
  };

  const removeCompletedSubject = (subject) => {
    setCompletedSubjects(completedSubjects.filter(s => s !== subject));
  };

  const addLanguage = () => {
    if (newLanguage && !languages.includes(newLanguage)) {
      setLanguages([...languages, newLanguage]);
      setNewLanguage('');
    }
  };

  const removeLanguage = (lang) => {
    if (languages.length > 1) {
      const updated = languages.filter(l => l !== lang);
      setLanguages(updated);
      if (preferredLanguage === lang) {
        setPreferredLanguage(updated[0]);
      }
    }
  };

  const toggleDuration = (duration) => {
    if (sessionDurations.includes(duration)) {
      if (sessionDurations.length > 1) {
        setSessionDurations(sessionDurations.filter(d => d !== duration));
      }
    } else {
      setSessionDurations([...sessionDurations, duration].sort((a, b) => a - b));
    }
  };

  // Availability helpers
  const addAvailabilitySlot = (day) => {
    const daySlots = availability[day] || [];
    setAvailability({
      ...availability,
      [day]: [...daySlots, { start: '09:00', end: '17:00' }]
    });
  };

  const removeAvailabilitySlot = (day, index) => {
    const daySlots = [...(availability[day] || [])];
    daySlots.splice(index, 1);
    if (daySlots.length === 0) {
      const updated = { ...availability };
      delete updated[day];
      setAvailability(updated);
    } else {
      setAvailability({ ...availability, [day]: daySlots });
    }
  };

  const updateAvailabilitySlot = (day, index, field, value) => {
    const daySlots = [...(availability[day] || [])];
    daySlots[index] = { ...daySlots[index], [field]: value };
    setAvailability({ ...availability, [day]: daySlots });
  };

  // Validation
  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!bio.trim() || bio.trim().length < 50) return 'Bio must be at least 50 characters.';
        if (languages.length === 0) return 'Select at least one language.';
        return null;
      case 2:
        if (subjects.length === 0) return 'Add at least one subject you can tutor.';
        if (!hourlyRate || parseFloat(hourlyRate) < 20) return 'Minimum hourly rate is $20.';
        if (parseFloat(hourlyRate) > 200) return 'Maximum hourly rate is $200.';
        if (sessionDurations.length === 0) return 'Select at least one session duration.';
        return null;
      case 3:
        return null; // Availability is optional during registration
      default:
        return null;
    }
  };

  const handleNext = () => {
    const validationError = validateStep(currentStep);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    setError('');
    setCurrentStep(currentStep - 1);
  };

  // Submit registration
  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    try {
      const data = {
        bio: bio.trim(),
        education_level: educationLevel,
        languages,
        preferred_language: preferredLanguage,
        subjects,
        completed_subjects: completedSubjects,
        hourly_rate: parseFloat(hourlyRate),
        session_durations: sessionDurations,
        timezone,
        availability,
      };

      await registerAsTutor(data);

      // After registration, move to Stripe onboarding step
      setCurrentStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Credential upload handler
  const handleCredentialUpload = async (credentialType, file) => {
    setUploadingCredential(true);
    setError('');
    try {
      const res = await uploadTutorCredential(credentialType, file);
      setCredentials(prev => [...prev, res.data.credential]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload credential.');
    }
    setUploadingCredential(false);
  };

  const credentialTypes = [
    { value: 'student_card', label: 'Student Card', description: 'Photo of your university student card (front)' },
    { value: 'enrolment_proof', label: 'Enrolment Proof', description: 'Screenshot or PDF from university portal' },
    { value: 'transcript', label: 'Transcript', description: 'Official or unofficial transcript showing completed subjects' },
    { value: 'score_report', label: 'Test Score Report', description: 'Official score report for SAT/MCAT/GAMSAT etc.' },
    { value: 'wwcc', label: 'Working With Children Check', description: 'Required for tutoring high school students (AU)' },
  ];

  const getUploadedTypes = () => credentials.map(c => c.type);

  // Stripe onboarding redirect
  const handleStripeOnboarding = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await getOnboardingLink();
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to get onboarding link. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-500" />
        </div>
      </Layout>
    );
  }

  const steps = [
    { number: 1, label: 'Profile' },
    { number: 2, label: 'Subjects & Pricing' },
    { number: 3, label: 'Availability' },
    { number: 4, label: 'Verification' },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a192f 0%, #0d2847 100%)' }}>
          <div className="max-w-3xl mx-auto px-4 py-10 text-center">
            <div className="flex justify-center mb-3"><Icon3D icon={GraduationCap} colour="rose" size="lg" /></div>
            <h1 className="text-3xl font-bold text-white mb-2">Become a Tutor</h1>
            <p className="text-slate-300">
              Share your knowledge, help students succeed, and earn up to $75/hr.
            </p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="max-w-3xl mx-auto px-4 -mt-4 relative z-10">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              {steps.map((step, i) => (
                <React.Fragment key={step.number}>
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                        currentStep > step.number
                          ? 'bg-green-500 text-white'
                          : currentStep === step.number
                            ? 'text-white'
                            : 'bg-slate-200 text-slate-500'
                      }`}
                      style={currentStep === step.number ? { background: 'linear-gradient(135deg, rgb(6, 182, 212), rgb(8, 145, 178))' } : {}}
                    >
                      {currentStep > step.number ? <Check className="w-4 h-4" /> : step.number}
                    </div>
                    <span className={`text-sm font-medium hidden sm:block ${
                      currentStep >= step.number ? 'text-slate-900' : 'text-slate-400'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  {i < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-3 ${
                      currentStep > step.number ? 'bg-green-500' : 'bg-slate-200'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="max-w-3xl mx-auto px-4 mt-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Form Content */}
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">

            {/* Step 1: Profile */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-1">Your Profile</h2>
                  <p className="text-sm text-slate-500">Tell students about yourself and your teaching background.</p>
                </div>

                {/* Education Level */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Education Level
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {EDUCATION_LEVELS.map(level => (
                      <button
                        key={level.value}
                        type="button"
                        onClick={() => setEducationLevel(level.value)}
                        className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          educationLevel === level.value
                            ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                            : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        {level.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Bio */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Bio <span className="text-slate-400">({bio.length}/500)</span>
                  </label>
                  <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value.slice(0, 500))}
                    rows={4}
                    placeholder="Tell students about your academic background, teaching experience, and what makes you a great tutor..."
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-cyan-400 focus:border-transparent resize-none"
                  />
                  {bio.length < 50 && (
                    <p className="text-xs text-slate-400 mt-1">Minimum 50 characters ({50 - bio.length} more needed)</p>
                  )}
                </div>

                {/* Languages */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Globe className="w-4 h-4 inline mr-1" />
                    Languages You Speak
                  </label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {languages.map(lang => (
                      <span
                        key={lang}
                        className="px-3 py-1.5 rounded-full text-sm bg-cyan-50 text-cyan-700 border border-cyan-200 flex items-center gap-1.5"
                      >
                        {lang}
                        {lang === preferredLanguage && (
                          <span className="text-xs bg-cyan-200 text-cyan-800 px-1.5 py-0.5 rounded-full">Primary</span>
                        )}
                        {languages.length > 1 && (
                          <button onClick={() => removeLanguage(lang)} className="hover:text-red-500">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={newLanguage}
                      onChange={(e) => setNewLanguage(e.target.value)}
                      className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-900"
                    >
                      <option value="">Add a language...</option>
                      {LANGUAGES.filter(l => !languages.includes(l)).map(l => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={addLanguage}
                      disabled={!newLanguage}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 transition-all"
                      style={{ background: 'linear-gradient(135deg, rgb(6, 182, 212), rgb(8, 145, 178))' }}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Preferred Language */}
                  {languages.length > 1 && (
                    <div className="mt-3">
                      <label className="block text-xs text-slate-500 mb-1">Primary Teaching Language</label>
                      <select
                        value={preferredLanguage}
                        onChange={(e) => setPreferredLanguage(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-900"
                      >
                        {languages.map(l => (
                          <option key={l} value={l}>{l}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Subjects & Pricing */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-1">Subjects & Pricing</h2>
                  <p className="text-sm text-slate-500">What subjects do you teach and how much do you charge?</p>
                </div>

                {/* Subjects */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <BookOpen className="w-4 h-4 inline mr-1" />
                    Subjects You Tutor
                  </label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {subjects.map(s => (
                      <span
                        key={s}
                        className="px-3 py-1.5 rounded-full text-sm bg-cyan-50 text-cyan-700 border border-cyan-200 flex items-center gap-1.5"
                      >
                        {s}
                        <button onClick={() => removeSubject(s)} className="hover:text-red-500">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                    {subjects.length === 0 && (
                      <p className="text-sm text-slate-400">No subjects added yet</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newSubject}
                      onChange={(e) => setNewSubject(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSubject())}
                      placeholder="e.g., Cardiology, Chemistry, SAT Math..."
                      className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-900 placeholder-slate-400"
                    />
                    <button
                      type="button"
                      onClick={addSubject}
                      disabled={!newSubject.trim()}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 transition-all"
                      style={{ background: 'linear-gradient(135deg, rgb(6, 182, 212), rgb(8, 145, 178))' }}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Completed Subjects (for cross-level tutoring) */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Completed Subjects
                  </label>
                  <p className="text-xs text-slate-400 mb-2">
                    Subjects you've fully completed — allows you to tutor lower-level students in these.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {completedSubjects.map(s => (
                      <span
                        key={s}
                        className="px-3 py-1.5 rounded-full text-sm bg-green-50 text-green-700 border border-green-200 flex items-center gap-1.5"
                      >
                        {s}
                        <button onClick={() => removeCompletedSubject(s)} className="hover:text-red-500">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                    {completedSubjects.length === 0 && (
                      <p className="text-sm text-slate-400">None added</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newCompletedSubject}
                      onChange={(e) => setNewCompletedSubject(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCompletedSubject())}
                      placeholder="e.g., Biology, Year 12 Chemistry..."
                      className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-900 placeholder-slate-400"
                    />
                    <button
                      type="button"
                      onClick={addCompletedSubject}
                      disabled={!newCompletedSubject.trim()}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 transition-all"
                      style={{ background: 'linear-gradient(135deg, rgb(6, 182, 212), rgb(8, 145, 178))' }}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Hourly Rate */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <DollarSign className="w-4 h-4 inline mr-1" />
                    Hourly Rate (AUD)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">$</span>
                    <input
                      type="number"
                      value={hourlyRate}
                      onChange={(e) => setHourlyRate(e.target.value)}
                      min="20"
                      max="200"
                      step="5"
                      className="w-full pl-8 pr-16 py-3 rounded-lg border border-slate-300 text-slate-900 focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">/hour</span>
                  </div>
                  <div className="flex justify-between mt-2">
                    <p className="text-xs text-slate-400">Min: $20/hr</p>
                    <p className="text-xs text-cyan-600 font-medium">
                      You earn: ${(parseFloat(hourlyRate || 0) * 0.75).toFixed(2)}/hr (75%)
                    </p>
                  </div>
                </div>

                {/* Session Durations */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Session Durations Offered
                  </label>
                  <div className="flex gap-3">
                    {SESSION_DURATION_OPTIONS.map(d => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => toggleDuration(d)}
                        className={`flex-1 p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          sessionDurations.includes(d)
                            ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                            : 'border-slate-200 text-slate-500 hover:border-slate-300'
                        }`}
                      >
                        {d} min
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Availability */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-1">Availability</h2>
                  <p className="text-sm text-slate-500">Set your weekly availability. You can update this later from your dashboard.</p>
                </div>

                {/* Timezone */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Your Timezone
                  </label>
                  <input
                    type="text"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 text-slate-900 bg-slate-50"
                    readOnly
                  />
                  <p className="text-xs text-slate-400 mt-1">Detected automatically from your browser</p>
                </div>

                {/* Availability Grid */}
                <div className="space-y-3">
                  {DAYS_OF_WEEK.map(day => (
                    <div key={day} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-slate-700 capitalize">{day}</h4>
                        <button
                          type="button"
                          onClick={() => addAvailabilitySlot(day)}
                          className="text-xs text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                        >
                          <Plus className="w-3.5 h-3.5" /> Add slot
                        </button>
                      </div>
                      {(availability[day] || []).length === 0 ? (
                        <p className="text-sm text-slate-400">Not available</p>
                      ) : (
                        <div className="space-y-2">
                          {(availability[day] || []).map((slot, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <input
                                type="time"
                                value={slot.start}
                                onChange={(e) => updateAvailabilitySlot(day, i, 'start', e.target.value)}
                                className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm text-slate-900"
                              />
                              <span className="text-slate-400">to</span>
                              <input
                                type="time"
                                value={slot.end}
                                onChange={(e) => updateAvailabilitySlot(day, i, 'end', e.target.value)}
                                className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm text-slate-900"
                              />
                              <button
                                type="button"
                                onClick={() => removeAvailabilitySlot(day, i)}
                                className="p-1 text-slate-400 hover:text-red-500"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Stripe Verification */}
            {currentStep === 4 && (
              <div className="space-y-6 text-center">
                <div>
                  <div className="flex justify-center mb-4"><Icon3D icon={Shield} colour="green" size="xl" /></div>
                  <h2 className="text-xl font-bold text-slate-900 mb-2">Identity Verification</h2>
                  <p className="text-sm text-slate-500 max-w-md mx-auto">
                    To protect students and comply with regulations, we use Stripe to verify your identity
                    and set up secure payouts to your bank account.
                  </p>
                </div>

                {/* Stripe Status */}
                {stripeStatus && (
                  <div className="bg-slate-50 rounded-xl p-4 text-left max-w-md mx-auto">
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Verification Status</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Details Submitted</span>
                        <span className={stripeStatus.details_submitted ? 'text-green-600' : 'text-amber-500'}>
                          {stripeStatus.details_submitted ? 'Yes' : 'Pending'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Payments Enabled</span>
                        <span className={stripeStatus.charges_enabled ? 'text-green-600' : 'text-amber-500'}>
                          {stripeStatus.charges_enabled ? 'Yes' : 'Pending'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Payouts Enabled</span>
                        <span className={stripeStatus.payouts_enabled ? 'text-green-600' : 'text-amber-500'}>
                          {stripeStatus.payouts_enabled ? 'Yes' : 'Pending'}
                        </span>
                      </div>
                      {stripeStatus.verified_name && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Verified Name</span>
                          <span className="text-slate-700 font-medium">{stripeStatus.verified_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Credential Uploads */}
                <div className="bg-white border border-slate-200 rounded-xl p-5 text-left max-w-lg mx-auto">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-5 h-5 text-slate-600" />
                    <h4 className="text-sm font-semibold text-slate-700">Upload Credentials</h4>
                  </div>
                  <p className="text-xs text-slate-500 mb-4">
                    Upload proof of your qualifications. Admin will review these before approving your profile.
                  </p>

                  {/* Already uploaded */}
                  {credentials.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {credentials.map((cred, i) => (
                        <div key={i} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-500" />
                            <span className="text-sm text-slate-700 capitalize">{cred.type?.replace('_', ' ')}</span>
                            {cred.filename && <span className="text-xs text-slate-400">({cred.filename})</span>}
                          </div>
                          {cred.verified ? (
                            <span className="text-xs text-green-600 font-medium">Verified</span>
                          ) : (
                            <span className="text-xs text-amber-500">Pending review</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload buttons for each type */}
                  <div className="space-y-2">
                    {credentialTypes
                      .filter(ct => !getUploadedTypes().includes(ct.value))
                      .map((ct) => (
                        <label
                          key={ct.value}
                          className="flex items-center gap-3 p-3 border border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition"
                        >
                          <Upload className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700">{ct.label}</p>
                            <p className="text-xs text-slate-400">{ct.description}</p>
                          </div>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.webp"
                            className="hidden"
                            disabled={uploadingCredential}
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                handleCredentialUpload(ct.value, e.target.files[0]);
                              }
                            }}
                          />
                        </label>
                      ))}
                  </div>

                  {uploadingCredential && (
                    <div className="flex items-center gap-2 mt-3 text-sm text-cyan-600">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </div>
                  )}

                  <p className="text-xs text-slate-400 mt-3">
                    Accepted formats: PDF, JPG, PNG. Max 10MB per file.
                  </p>
                </div>

                {/* What Stripe Verifies */}
                <div className="bg-cyan-50 rounded-xl p-5 text-left max-w-md mx-auto">
                  <h4 className="text-sm font-semibold text-cyan-800 mb-3">What Stripe Verifies</h4>
                  <ul className="space-y-2">
                    {[
                      'Government-issued ID',
                      'Legal name verification',
                      'Bank account for payouts',
                      'Contact information'
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-cyan-700">
                        <Check className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Fees Info */}
                <div className="bg-amber-50 rounded-xl p-5 text-left max-w-md mx-auto">
                  <h4 className="text-sm font-semibold text-amber-800 mb-2">Platform Fees</h4>
                  <ul className="space-y-1.5 text-sm text-amber-700">
                    <li>25% platform commission on each session</li>
                    <li>3% booking fee charged to student (covers card processing)</li>
                    <li>Stripe Connect: $2/month active account fee</li>
                    <li>Payout fee: 0.25% + $0.25 per transfer</li>
                  </ul>
                </div>

                <button
                  onClick={handleStripeOnboarding}
                  disabled={submitting}
                  className="px-8 py-3 rounded-xl text-white font-semibold transition-all hover:opacity-90 disabled:opacity-50 flex items-center gap-2 mx-auto"
                  style={{ background: 'linear-gradient(135deg, rgb(6, 182, 212), rgb(8, 145, 178))', boxShadow: 'var(--shadow-3d-btn)' }}
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <ExternalLink className="w-5 h-5" />
                  )}
                  {submitting ? 'Redirecting...' : 'Continue to Stripe Verification'}
                </button>

                <p className="text-xs text-slate-400 max-w-sm mx-auto">
                  You'll be redirected to Stripe's secure portal to complete identity verification.
                  This typically takes 2-3 minutes.
                </p>
              </div>
            )}

            {/* Navigation Buttons */}
            {currentStep < 4 && (
              <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
                {currentStep > 1 ? (
                  <button
                    onClick={handleBack}
                    className="px-5 py-2.5 rounded-lg text-slate-600 font-medium hover:bg-slate-100 flex items-center gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" /> Back
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/tutoring')}
                    className="px-5 py-2.5 rounded-lg text-slate-600 font-medium hover:bg-slate-100 flex items-center gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" /> Cancel
                  </button>
                )}

                {currentStep < 3 ? (
                  <button
                    onClick={handleNext}
                    className="px-6 py-2.5 rounded-lg text-white font-semibold flex items-center gap-1 hover:opacity-90 transition-all"
                    style={{ background: 'linear-gradient(135deg, rgb(6, 182, 212), rgb(8, 145, 178))', boxShadow: 'var(--shadow-3d-btn)' }}
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="px-6 py-2.5 rounded-lg text-white font-semibold flex items-center gap-2 hover:opacity-90 transition-all disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, rgb(6, 182, 212), rgb(8, 145, 178))', boxShadow: 'var(--shadow-3d-btn)' }}
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    {submitting ? 'Registering...' : 'Register & Continue'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TutorRegistrationPage;
